/**
 * Dashboard Widget Configuration & Factory
 * 
 * Phase 3-4: UserType-based widget system
 * Phase 5+: Will transition to permission-based system
 * 
 * Widget Registry: Centralized configuration for all dashboard widgets
 * getDashboardWidgets: Factory function to get filtered widgets for a user
 */

// ============================================================================
// WIDGET TYPES ENUM (Widget Kategorileri - Sabit)
// ============================================================================

/**
 * Widget kategorileri - bunlar değişmeyecek
 * userTypes (individual, company_owner, etc.) ile karıştırılmamalı!
 * 
 * WIDGET_TYPES: Widget'ların hangi kategoride olduğunu belirtir
 * userTypes: Kullanıcının hangi tipte olduğunu belirtir (Phase 4 refactoring)
 */
export const WIDGET_TYPES = {
  COMMON: 'common',      // Tüm kullanıcılar için widget'lar
  USER: 'user',          // Bireysel kullanıcı kategorisi (userType: individual)
  COMPANY: 'company',    // Şirket kategorisi (userType: company_owner, company_member)
  ADMIN: 'admin'         // Admin kategorisi (userType: system_admin) - Phase 5
};

// ============================================================================
// WIDGET REGISTRY
// ============================================================================

/**
 * Widget Registry - Centralized widget configuration
 * 
 * Each widget has:
 * - id: Unique identifier
 * - component: Component name (string) for dynamic import
 * - type: Widget type (common/user/company/admin)
 * - order: Display order (lower = first)
 * - gridSize: MUI Grid v2 size prop (xs, sm, md, lg, xl)
 * - userTypes: Array of userTypes that can see this widget (Phase 4 refactoring)
 * - condition: Optional function to check if widget should be shown
 */
export const WIDGET_REGISTRY = {
  // ===== COMMON WIDGETS (ALL ROLES) =====
  
  welcome: {
    id: 'welcome',
    component: 'WelcomeCard',
    type: WIDGET_TYPES.COMMON,
    order: 0,
    gridSize: { xs: 12 },
    userTypes: ['individual', 'company_owner', 'company_member', 'system_admin'] // All user types
  },
  
  emailVerification: {
    id: 'emailVerification',
    component: 'EmailVerificationBanner',
    type: WIDGET_TYPES.COMMON,
    order: 1,
    gridSize: { xs: 12 },
    userTypes: ['individual', 'company_owner', 'company_member', 'system_admin'],
    condition: (user) => !user?.isEmailVerified // Only show if email not verified
  },

  quickActions: {
    id: 'quickActions',
    component: 'QuickActionsWidget',
    type: WIDGET_TYPES.COMMON,
    order: 1.5,
    gridSize: { xs: 12 },
    userTypes: ['individual', 'company_owner', 'company_member', 'system_admin']
  },

  // ===== USER WIDGETS (userType: individual, company_member) =====
  
  myDocuments: {
    id: 'myDocuments',
    component: 'MyDocumentsWidget',
    type: WIDGET_TYPES.USER,
    order: 2,
    gridSize: { xs: 12, md: 6 },
    userTypes: ['individual', 'company_member'] // company_member eklendi - normal user gibi görebilmeli
  },

  templates: {
    id: 'templates',
    component: 'TemplatesWidget',
    type: WIDGET_TYPES.USER,
    order: 3,
    gridSize: { xs: 12, md: 6 },
    userTypes: ['individual', 'company_member'] // company_member eklendi - normal user gibi görebilmeli
  },

  recentDocuments: {
    id: 'recentDocuments',
    component: 'RecentDocumentsWidget',
    type: WIDGET_TYPES.USER,
    order: 3.5,
    gridSize: { xs: 12, md: 6 },
    userTypes: ['individual', 'company_member'] // company_member eklendi - normal user gibi görebilmeli
  },

  recentActivity: {
    id: 'recentActivity',
    component: 'RecentActivityWidget',
    type: WIDGET_TYPES.USER,
    order: 4,
    gridSize: { xs: 12 },
    userTypes: ['individual', 'company_member'] // company_member eklendi - normal user gibi görebilmeli
  },

  // ===== COMPANY WIDGETS (userType: company_owner) =====
  
  companyStats: {
    id: 'companyStats',
    component: 'CompanyStatsWidget',
    type: WIDGET_TYPES.COMPANY,
    order: 2,
    gridSize: { xs: 12 },
    userTypes: ['company_owner'] // company_member kaldırıldı - istatistikler sadece owner için
  },

  usersSummary: {
    id: 'usersSummary',
    component: 'UsersSummaryWidget',
    type: WIDGET_TYPES.COMPANY,
    order: 3,
    gridSize: { xs: 12, md: 6 },
    userTypes: ['company_owner']
  },

  companyInfo: {
    id: 'companyInfo',
    component: 'CompanyInfoWidget',
    type: WIDGET_TYPES.COMPANY,
    order: 4,
    gridSize: { xs: 12, md: 6 },
    userTypes: ['company_owner']
  },

  invites: {
    id: 'invites',
    component: 'InvitesWidget',
    type: WIDGET_TYPES.COMPANY,
    order: 5,
    gridSize: { xs: 12, md: 6 },
    userTypes: ['company_owner']
  },

  // ===== ADMIN WIDGETS (userType: system_admin) - Phase 5 =====
  
  systemStats: {
    id: 'systemStats',
    component: 'SystemStatsWidget',
    type: WIDGET_TYPES.ADMIN,
    order: 2,
    gridSize: { xs: 12 },
    userTypes: ['system_admin']
  },

  usersOverview: {
    id: 'usersOverview',
    component: 'UsersOverviewWidget',
    type: WIDGET_TYPES.ADMIN,
    order: 3,
    gridSize: { xs: 12 },
    userTypes: ['system_admin']
  },

  // ===== PHASE 7: CREDIT SYSTEM WIDGETS =====
  
  creditBalance: {
    id: 'creditBalance',
    component: 'CreditBalanceWidget',
    type: WIDGET_TYPES.COMMON,
    order: 1.6,
    gridSize: { xs: 12, md: 4 },
    userTypes: ['individual', 'company_owner','system_admin'],
  },

  // creditReports: {
  //   id: 'creditReports',
  //   component: 'CreditReportsWidget',
  //   type: WIDGET_TYPES.ADMIN,
  //   order: 2.5,
  //   gridSize: { xs: 12, md: 6 },
  //   userTypes: ['system_admin'],
  //   permissions: ['credits.reports', 'systemadminpermission'] // Permission-based visibility
  // },

  companyCredit: {
    id: 'companyCredit',
    component: 'CompanyCreditWidget',
    type: WIDGET_TYPES.COMPANY,
    order: 2.5,
    gridSize: { xs: 12, md: 6 },
    userTypes: ['company_owner'],
    permissions: ['credits.view', 'companyadminpermission'] // Permission-based visibility
  },

  creditHistory: {
    id: 'creditHistory',
    component: 'CreditHistoryWidget',
    type: WIDGET_TYPES.USER,
    order: 4.5,
    gridSize: { xs: 12, md: 6 },
    userTypes: ['individual', 'company_owner'],
    permissions: ['credits.view', 'companyadminpermission'] // Permission-based visibility
  }
};

// ============================================================================
// WIDGET FACTORY FUNCTION
// ============================================================================

/**
 * Get dashboard widgets for a user
 * 
 * Phase 3-4: UserType-based filtering (Phase 4 refactoring: userType → userType)
 * Phase 5+: Will be replaced with permission-based filtering
 * 
 * @param {string} userType - User's type (individual/company_owner/company_member/system_admin)
 * @param {Object} user - Full user object (for conditional checks)
 * @returns {Array} Filtered and sorted widget configurations
 */
export const getDashboardWidgets = (userType, user) => {
  // Guard: No userType or user provided
  if (!userType || !user) {
    console.warn('getDashboardWidgets: userType or user is missing');
    return [];
  }

  return Object.values(WIDGET_REGISTRY)
    .filter(widget => {
      // Filter 1: Check userType (Phase 4 refactoring)
      if (widget.userTypes && !widget.userTypes.includes(userType)) {
        return false;
      }

      // Filter 2: Check condition (e.g., email verification banner)
      if (widget.condition && !widget.condition(user)) {
        return false;
      }

      // Widget passed all filters
      return true;
    })
    .sort((a, b) => a.order - b.order); // Sort by display order
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get widget by ID
 * @param {string} widgetId - Widget ID
 * @returns {Object|null} Widget configuration or null
 */
export const getWidgetById = (widgetId) => {
  return WIDGET_REGISTRY[widgetId] || null;
};

/**
 * Get all widgets by type
 * @param {string} type - Widget type (common/user/company/admin)
 * @returns {Array} Widget configurations
 */
export const getWidgetsByType = (type) => {
  return Object.values(WIDGET_REGISTRY)
    .filter(widget => widget.type === type);
};

/**
 * Check if user can see a specific widget
 * @param {string} widgetId - Widget ID
 * @param {string} userType - User's type (Phase 4 refactoring)
 * @param {Object} user - Full user object
 * @returns {boolean} True if user can see widget
 */
export const canUserSeeWidget = (widgetId, userType, user) => {
  const widget = getWidgetById(widgetId);
  if (!widget) return false;

  // Check userType (Phase 4 refactoring)
  if (widget.userTypes && !widget.userTypes.includes(userType)) {
    return false;
  }

  // Check condition
  if (widget.condition && !widget.condition(user)) {
    return false;
  }

  return true;
};

// ============================================================================
// FUTURE: Phase 5+ Permission-Based Widget System
// ============================================================================

/**
 * Future: Get dashboard widgets based on permissions (Phase 5+)
 * 
 * This function will replace getDashboardWidgets in Phase 5.
 * Instead of userType-based filtering, it will use user permissions.
 * 
 * @param {Array} userPermissions - User's permission array
 * @param {Object} user - Full user object
 * @returns {Array} Filtered and sorted widget configurations
 */
export const getDashboardWidgetsByPermissions = (userPermissions, user) => {
  // TODO: Phase 5 implementation
  // Will check widget.permission instead of widget.userTypes
  console.log('getDashboardWidgetsByPermissions: Phase 5 feature - not implemented yet');
  return [];
};

